import bowser from "bowser";

import { inCalendar, inVideocall } from "../browserExtension";

const parser = bowser.getParser(window.navigator.userAgent);

/**
 * Determine if the app is running within an Android/iOS wrapper via Capacitor
 */
export const isNative = window.Capacitor?.isNativePlatform();

/**
 * Determine the platform running the native app via Capacitor
 */
export function getPlatform(): "web" | "ios" | "android" {
	return window.Capacitor?.getPlatform() as "web" | "ios" | "android";
}

/**
 * Determine if the app is running within an iOS wrapper via Capacitor
 */
export const isiOSApp = getPlatform() === "ios";

/**
 * Determine if the app is running within an Android wrapper via Capacitor
 */
export const isAndroidApp = getPlatform() === "android";

/**
 * Determine if the app is running in the Fellow desktop app
 */
// todo-zm: `use window.electron?.desktopOsInfo?.name`
export const isDesktopApp = navigator.userAgent.includes("Fellow");

/**
 * Determine if the app is running on Windows
 */
// todo-zm: `use window.electron?.desktopOsInfo?.name`
export const isWindows = parser.is("Windows");

/**
 * Determine if the app is running on MacOS
 */
// todo-zm: `use window.electron?.desktopOsInfo?.name`
export const isMacos = parser.is("MacOS");

/**
 * Determine if the app is running on Linux
 */
// todo-zm: remove this as we don't support linux
export const isLinux = parser.is("Linux");

/**
 * Determine if the app is running on an Android web browser
 */
export const isAndroid = parser.is("Android");

/**
 * Determine if the app is running on an iOS web browser
 */
export const isiOS = parser.is("iOS");

/**
 * Determine if the app is running in Chrome
 */
export const isChrome = parser.is("Chrome");

/**
 * Determine if the app is running in Firefox
 */
export const isFirefox = parser.is("Firefox");

/**
 * Determine if the app is running in Safari
 */
export const isSafari = parser.is("Safari");

/**
 * Cross-browser print function that works with Safari and other browsers.
 * Safari requires document.execCommand('print') while other browsers use window.print()
 */
export function printPage() {
	if (isSafari) {
		document.execCommand("print");
	} else {
		window.print();
	}
}

/**
 * Determine if the app is running in a web browser
 */
export const isWeb = !isNative && !isDesktopApp;

export type Platform = "browser" | "macos" | "windows" | "other";
export type DesktopPlatform = Exclude<Platform, "browser" | "other">;
export const platform: Platform = isWeb ? "browser" : isDesktopApp ? (isMacos ? "macos" : "windows") : "other";

/**
 * Determine if the app is running within Zoom
 */
export const isZoom = navigator.userAgent.includes("ZoomApps/1");

/**
 * Determine if the app is running within the MS Teams desktop app
 */
export const isTeams = window.location.pathname.startsWith("/apps/teams");

/**
 * Determine if the app is running on a Mobile browser
 */
export const isMobile = parser.getPlatformType() === "mobile";

/**
 * Determine if the app is running on a Tablet browser
 */
export const isTablet = parser.getPlatformType() === "tablet";

/**
 * Determine if the app is running on a Desktop browser
 */
export const isDesktop = parser.getPlatformType() === "desktop";

/**
 * STOP! Use `@media not (pointer: fine)` in CSS if you can help it. -Wal
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/@media/pointer
 *
 * Determine if the hardware device does not have a fine pointer. This usually means it's a touchscreen device.
 */
export const isTouchScreen = !window.matchMedia("(pointer: fine)").matches;

/**
 * Determine if the app is running in Companion Mode
 */
export const inCompanionMode = navigator.userAgent.includes("FellowCompanionMode");

/**
 * Determine if the app is running in a Zoom Meeting
 */
export const inZoomMeeting = sessionStorage.getItem("lib.zoomContext") === "meeting";

/**
 * Determine if the app is running in Google Meet, MS Teams (via the browser extension) or Zoom Meeting
 */
export const inVideocallMode = inCompanionMode || inVideocall || inZoomMeeting;

/**
 * Determine if the app is running in Google Calendar or MS Outlook
 */
export const inCalendarMode = inCalendar;

/**
 * Determine if the app should be rendering the _new_ Companion Mode (i.e. /companion-mode/)
 */
export const inNewCompanionMode =
	window.location.pathname.includes("/companion-mode") || inVideocallMode || inCalendarMode || isZoom || isTeams;

/** Symbol for Command key on macOS, or Control key on other platforms */
export const CtrlOrCmd = isMacos ? "⌘" : "Ctrl";
export const UpOrOption = isSafari ? "⌥" : "⇧";

/**
 * Return true if the Command key on macOS, or the Control key on other platforms,
 * was being held when `event` was fired.
 */
export const isCtrlOrCmd = (event: KeyboardEvent | MouseEvent): boolean => {
	if (isMacos) {
		return event.metaKey;
	}
	return event.ctrlKey;
};

/* List of app/extension links for this environment */
/* eslint-disable max-len */
export const playStoreLink = `https://play.app.goo.gl/?link=https://play.google.com/store/apps/details?id=${window.UNIVERSAL_LINKING_BUNDLE_ID}`;
export const appStoreLink = `https://apps.apple.com/ca/app/id${window.IOS_APP_STORE_ID}`;
export const desktopDownloadUri = `/desktop/download/${
	isMacos ? "darwin" : "win32"
}/latest/?utm_source=fellow_core&utm_medium=web`;
export const macOsArmDesktopDownloadUri =
	"/desktop/download/darwin_arm64/latest/?utm_source=fellow_core&utm_medium=web";
export const macOsX64DesktopDownloadUri = "/desktop/download/darwin_x64/latest/?utm_source=fellow_core&utm_medium=web";
export const desktopDownloadLink = `${window.location.origin}${desktopDownloadUri}`;
export const chromeStoreLink = `https://chromewebstore.google.com/detail/fellow-meeting-notes-agen/${window.CHROME_EXTENSION_ID}?utm_source=fellow_core&utm_medium=web`;

const firefoxExtensionProdLink =
	"https://addons.mozilla.org/firefox/addon/fellow/?utm_source=fellow_core&utm_medium=web";
const firefoxExtensionStagingLink = "https://firefoxaddons-staging.fellow.app/fellow-ext-firefox-latest.xpi";
export const mozillaStoreLink =
	window.ENVIRONMENT === "staging" ? firefoxExtensionStagingLink : firefoxExtensionProdLink;

export const msTeamsStoreLink = `https://appsource.microsoft.com/en-us/product/office/${window.MICROSOFT_TEAMS_APPSOURCE_ID}/?utm_source=fellow_core&utm_medium=web`;
export const slackAppLink = `https://slack.com/apps/${window.SLACK_APP_ID}/?utm_source=fellow_core&utm_medium=web`;
export const zapierZapLink = "https://zapier.com/apps/fellow/integrations/?utm_source=fellow_core&utm_medium=web";
export const asanaAppLink = "https://asana.com/apps/fellow/?utm_source=fellow_core&utm_medium=web";

const zoomStagingLink = `
	https://zoom.us/oauth/authorize?response_type=code&client_id=4KPmEWAMTRuhuQPs7hCraw
	&redirect_uri=https://staging.fellow.co/zoom/oauth/redirect
`;
const zoomProdLink = "https://marketplace.zoom.us/apps/PSqbhfgwRCe6MHUjwql6eA/?utm_source=fellow_core&utm_medium=web";
export const zoomStoreLink = window.ENVIRONMENT === "staging" ? zoomStagingLink : zoomProdLink;
/* eslint-enable max-len */
